import { useId, type ReactNode } from 'react';
import type { StackLike } from '@/lib/deployPlan';
import { cn } from '@/lib/utils';

export interface DeploySummaryProps {
  catalogName: string | null;
  stackName: string | null;
  /** Only cpu_limit, memory_mb, storage_gb and shared_cpu are read. */
  stack: StackLike | null;
  endpointLabel: string | null;
  /** Muted note under the target, e.g. "Auto-selected best fit". */
  endpointNote?: string | null;
  /** The app's domain; null hides the row (no domains known). */
  domainLabel?: string | null;
  /** `<subdomain>.<domain>` when a custom subdomain is set. */
  hostLabel?: string | null;
  externalUserId: string | null;
  instanceLabel: string | null;
  className?: string;
}

const Missing = () => <span className="text-muted-foreground">—</span>;

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-0.5">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="min-w-0 text-sm">{children}</dd>
    </div>
  );
}

const Truncated = ({ value, mono }: { value: string; mono?: boolean }) => (
  <span title={value} className={cn('block truncate', mono && 'font-mono text-xs')}>
    {value}
  </span>
);

const Chip = ({ children }: { children: ReactNode }) => (
  <span className="rounded-md border bg-muted/40 px-2 py-0.5 text-xs tabular-nums">{children}</span>
);

/** Use beside the wizard to show the choices made so far; read-only. */
export function DeploySummary({
  catalogName,
  stackName,
  stack,
  endpointLabel,
  endpointNote,
  domainLabel = null,
  hostLabel = null,
  externalUserId,
  instanceLabel,
  className,
}: DeploySummaryProps) {
  const headingId = useId();
  const catalogStack = catalogName && stackName ? `${catalogName} / ${stackName}` : null;

  return (
    <section
      aria-labelledby={headingId}
      className={cn(
        'space-y-4 rounded-xl bg-card p-4 text-card-foreground ring-1 ring-foreground/10 lg:sticky lg:top-4 lg:self-start',
        className,
      )}
    >
      <h2 id={headingId} className="text-sm font-semibold">
        Summary
      </h2>
      <dl className="space-y-3">
        <Row label="Catalog / Stack">{catalogStack ? <Truncated value={catalogStack} /> : <Missing />}</Row>
        <Row label="Resources">
          {stack ? (
            <div className="space-y-1">
              <div className="flex flex-wrap gap-1.5">
                <Chip>{stack.cpu_limit} cores</Chip>
                <Chip>{stack.memory_mb} MB</Chip>
                <Chip>{stack.storage_gb} GB</Chip>
              </div>
              {stack.shared_cpu && <p className="text-xs text-muted-foreground">Shared CPU</p>}
            </div>
          ) : (
            <Missing />
          )}
        </Row>
        <Row label="Target">
          {endpointLabel ? <Truncated value={endpointLabel} /> : <Missing />}
          {endpointLabel && endpointNote && <p className="text-xs text-muted-foreground">{endpointNote}</p>}
        </Row>
        {domainLabel && (
          <Row label="Domain">
            <Truncated value={domainLabel} />
          </Row>
        )}
        {hostLabel && (
          <Row label="Hostname">
            <Truncated value={hostLabel} mono />
          </Row>
        )}
        <Row label="Owner">{externalUserId ? <Truncated value={externalUserId} mono /> : <Missing />}</Row>
        <Row label="Name">{instanceLabel ? <Truncated value={instanceLabel} mono /> : <Missing />}</Row>
      </dl>
    </section>
  );
}
